import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Avatar,
  Chip,
  Tabs,
  Tab,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Calculate as CalculatorIcon,
  MenuBook as GuideIcon,
  Quiz as QuizIcon,
  GetApp as DownloadIcon,
  Help as HelpIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Public as StateIcon,
  Description as FormIcon,
  Timeline as ProcessIcon,
  ContactPhone as ContactIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Consumer Resources (existing)
const consumerResources = [
  {
    title: 'How Title Insurance Works in Woodbridge, NJ',
    description: 'Complete guide to title insurance for Middlesex County homebuyers.',
    icon: GuideIcon,
    category: 'Guide',
    type: 'document',
    audience: ['Homebuyers', 'First-time Buyers'],
    link: '/resources/title-insurance-woodbridge',
  },
  {
    title: 'Closing Checklist for Middlesex County Buyers',
    description: 'Essential checklist for buyers in Edison, Perth Amboy, New Brunswick and surrounding areas.',
    icon: DownloadIcon,
    category: 'Checklist',
    type: 'download',
    audience: ['Homebuyers'],
    link: '/resources/middlesex-county-closing-checklist',
  },
  {
    title: 'Closing Cost Calculator',
    description: 'Estimate your closing costs for New Jersey properties.',
    icon: CalculatorIcon,
    category: 'Calculator',
    type: 'tool',
    audience: ['Homebuyers', 'Sellers'],
    link: '/resources/closing-cost-calculator',
  },
  {
    title: 'New Jersey Real Estate Glossary',
    description: 'Essential terms for NJ property transactions including liens, deeds, and encumbrances.',
    icon: SchoolIcon,
    category: 'Reference',
    type: 'reference',
    audience: ['Everyone'],
    link: '/resources/nj-real-estate-glossary',
  },
  {
    title: 'What Should I Do With My Deed?',
    description: 'Important guidance on protecting and storing your property deed and title documents.',
    icon: HelpIcon,
    category: 'FAQ',
    type: 'reference',
    audience: ['Homeowners'],
    link: '/resources/deed-storage-guide',
  },
  {
    title: 'First-Time Homebuyer Guide for Middlesex County',
    description: 'Step-by-step guide for first-time buyers in Woodbridge, Edison, and surrounding areas.',
    icon: GuideIcon,
    category: 'Guide',
    type: 'document',
    audience: ['First-time Buyers'],
    link: '/resources/first-time-buyer-middlesex',
  },
  {
    title: 'Commercial Real Estate Title Process',
    description: 'Understanding title requirements for commercial properties in New Jersey.',
    icon: GuideIcon,
    category: 'Guide',
    type: 'document',
    audience: ['Real Estate Professionals', 'Investors'],
    link: '/resources/commercial-title-process',
  },
  {
    title: 'Title Insurance Claims Process',
    description: 'What to do if you need to file a title insurance claim and how we support you.',
    icon: HelpIcon,
    category: 'FAQ',
    type: 'reference',
    audience: ['Homeowners'],
    link: '/resources/title-claims-process',
  },
  {
    title: 'Escrow Process Explained',
    description: 'How escrow works in New Jersey real estate transactions and why it matters.',
    icon: SchoolIcon,
    category: 'Reference',
    type: 'reference',
    audience: ['Homebuyers', 'Sellers'],
    link: '/resources/escrow-process-explained',
  },
];

// Professional Industry Resources (new)
const professionalResources = [
  {
    title: 'NJ Recording Requirements by County',
    description: 'Complete guide to recording requirements, fees, and procedures for all 21 New Jersey counties.',
    icon: StateIcon,
    category: 'Legal',
    type: 'reference',
    audience: ['Title Professionals', 'Real Estate Attorneys', 'Paralegals'],
    link: '/resources/nj-recording-requirements',
  },
  {
    title: 'Title Search Best Practices Manual',
    description: 'Comprehensive manual covering title examination, chain of title analysis, and exception identification.',
    icon: ProcessIcon,
    category: 'Manual',
    type: 'document',
    audience: ['Title Professionals', 'Title Examiners'],
    link: '/resources/title-search-manual',
  },
  {
    title: 'NJ Title Insurance Rate Calculator',
    description: 'Professional calculator for title insurance premiums based on current NJ Department of Banking rates.',
    icon: CalculatorIcon,
    category: 'Calculator',
    type: 'tool',
    audience: ['Title Professionals', 'Real Estate Attorneys'],
    link: '/resources/nj-title-rate-calculator',
  },
  {
    title: 'Common Title Defects & Resolutions',
    description: 'Reference guide for identifying and resolving common title issues in New Jersey transactions.',
    icon: SecurityIcon,
    category: 'Reference',
    type: 'reference',
    audience: ['Title Professionals', 'Real Estate Attorneys'],
    link: '/resources/title-defects-guide',
  },
  {
    title: 'Commitment and Policy Templates',
    description: 'Downloadable templates for title commitments, policies, and related documentation.',
    icon: FormIcon,
    category: 'Template',
    type: 'download',
    audience: ['Title Professionals', 'Title Agencies'],
    link: '/resources/title-templates',
  },
  {
    title: 'ALTA Best Practices Compliance Checklist',
    description: 'Comprehensive checklist to ensure compliance with ALTA Best Practices and regulatory requirements.',
    icon: SecurityIcon,
    category: 'Checklist',
    type: 'download',
    audience: ['Title Professionals', 'Compliance Officers'],
    link: '/resources/alta-compliance-checklist',
  },
  {
    title: 'Escrow Account Management Guide',
    description: 'Best practices for escrow account management, reconciliation, and regulatory compliance.',
    icon: BusinessIcon,
    category: 'Manual',
    type: 'document',
    audience: ['Title Professionals', 'Escrow Officers'],
    link: '/resources/escrow-management-guide',
  },
  {
    title: 'Closing Coordination Workflow',
    description: 'Step-by-step workflow templates for coordinating closings between all parties.',
    icon: ProcessIcon,
    category: 'Template',
    type: 'download',
    audience: ['Title Professionals', 'Real Estate Professionals'],
    link: '/resources/closing-workflow-template',
  },
  {
    title: 'Professional Network & Resources',
    description: 'Connect with trusted real estate attorneys, lenders, and industry professionals in New Jersey.',
    icon: BusinessIcon,
    category: 'Reference',
    type: 'reference',
    audience: ['Title Professionals', 'Real Estate Professionals'],
    link: '/contact',
  },
];

const consumerCategories = ['All', 'Calculator', 'Guide', 'Reference', 'Checklist', 'FAQ'];
const professionalCategories = ['All', 'Legal', 'Manual', 'Calculator', 'Reference', 'Template', 'Checklist'];

const consumerAudiences = ['All', 'Homebuyers', 'First-time Buyers', 'Sellers', 'Homeowners', 'Real Estate Professionals', 'Investors'];
const professionalAudiences = ['All', 'Title Professionals', 'Real Estate Attorneys', 'Title Agents', 'Title Examiners', 'Compliance Officers', 'Escrow Officers', 'Paralegals', 'Loan Officers', 'Lenders'];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`resource-tabpanel-${index}`}
      aria-labelledby={`resource-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ResourceCenter: React.FC = () => {
  const [tabValue, setTabValue] = React.useState(0);
  const [selectedCategory, setSelectedCategory] = React.useState('All');
  const [selectedAudience, setSelectedAudience] = React.useState('All');
  const [searchQuery, setSearchQuery] = React.useState('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSelectedCategory('All');
    setSelectedAudience('All');
    setSearchQuery('');
  };

  const currentResources = tabValue === 0 ? consumerResources : professionalResources;
  const currentCategories = tabValue === 0 ? consumerCategories : professionalCategories;
  const currentAudiences = tabValue === 0 ? consumerAudiences : professionalAudiences;

  const filteredResources = currentResources.filter(resource => {
    const categoryMatch = selectedCategory === 'All' || resource.category === selectedCategory;
    const audienceMatch = selectedAudience === 'All' || resource.audience.includes(selectedAudience);
    const searchMatch = searchQuery === '' || 
      resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.audience.some(aud => aud.toLowerCase().includes(searchQuery.toLowerCase()));
    
    return categoryMatch && audienceMatch && searchMatch;
  });

  return (
    <>
      <Helmet>
        <title>Resource Center - MAD Title Agency LLC | NJ Real Estate Education & Professional Tools</title>
        <meta
          name="description"
          content="Comprehensive resource center for New Jersey real estate professionals and consumers. Title insurance guides, professional tools, compliance resources, and educational materials."
        />
        <meta
          name="keywords"
          content="New Jersey title resources, title insurance tools, NJ recording requirements, title professional education, ALTA compliance, real estate law updates"
        />
        <link rel="canonical" href="https://madtitle.pro/resources" />
      </Helmet>

      {/* Hero Section */}
      <Box
        sx={{
          backgroundColor: '#1e3a8a',
          color: 'white',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                color: 'white',
              }}
            >
              Resource Center
            </Typography>
            <Typography variant="h5" sx={{ maxWidth: 800, mx: 'auto', opacity: 0.9, color: '#94a3b8', mb: 4 }}>
              Comprehensive tools, guides, and resources for real estate professionals and consumers
            </Typography>
            
            {/* Search Bar */}
            <Box sx={{ maxWidth: 600, mx: 'auto' }}>
              <TextField
                fullWidth
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'white' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.5)',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#f59e0b',
                    },
                    '& input::placeholder': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                  },
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Resource Type Tabs */}
        <Box sx={{ mb: 4 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                fontSize: '1.1rem',
                fontWeight: 600,
                py: 2,
              },
            }}
          >
            <Tab label="Consumer Resources" />
            <Tab label="Professional Resources" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {/* Consumer Resources */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: '#1e3a8a' }}>
              For Homebuyers & Property Owners
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Essential resources to help you navigate your real estate transaction with confidence.
            </Typography>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Professional Resources */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: '#1e3a8a' }}>
              For Real Estate & Title Professionals
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Professional tools, compliance resources, and industry insights for title professionals, attorneys, and real estate agents.
            </Typography>
          </Box>
        </TabPanel>

        {/* Search Results Summary */}
        {searchQuery && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" color="text.secondary">
              {filteredResources.length} result{filteredResources.length !== 1 ? 's' : ''} found for "{searchQuery}"
            </Typography>
          </Box>
        )}

        {/* Filters */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Filter by Category:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
            {currentCategories.map((category) => (
              <Chip
                key={category}
                label={category}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
                color={selectedCategory === category ? 'primary' : 'default'}
                onClick={() => setSelectedCategory(category)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Filter by Audience:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {currentAudiences.map((audience) => (
              <Chip
                key={audience}
                label={audience}
                variant={selectedAudience === audience ? 'filled' : 'outlined'}
                color={selectedAudience === audience ? 'secondary' : 'default'}
                onClick={() => setSelectedAudience(audience)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>

        {/* Resources Grid */}
        <Grid container spacing={4}>
          {filteredResources.map((resource, index) => {
            const Icon = resource.icon;
            return (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  className="hover-lift"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          mr: 2,
                          backgroundColor: tabValue === 0 ? '#f59e0b' : '#1e3a8a',
                        }}
                      >
                        <Icon sx={{ fontSize: 24, color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Chip
                          label={resource.category}
                          size="small"
                          sx={{
                            backgroundColor: tabValue === 0 ? '#1e3a8a' : '#059669',
                            color: 'white',
                            mb: 1,
                          }}
                        />
                      </Box>
                    </Box>

                    <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                      {resource.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {resource.description}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {resource.audience.map((aud, audIndex) => (
                        <Chip
                          key={audIndex}
                          label={aud}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 24 }}
                        />
                      ))}
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      variant="contained"
                      component={Link}
                      to={resource.link}
                      sx={{
                        background: tabValue === 0
                          ? '#1e3a8a'
                          : 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                        width: '100%',
                      }}
                    >
                      {resource.type === 'download' ? 'Download' : 'Access Resource'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {filteredResources.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              {searchQuery 
                ? `No resources found matching "${searchQuery}" with the selected filters.`
                : 'No resources found for the selected filters.'
              }
            </Typography>
            <Button
              variant="outlined"
              onClick={() => {
                setSelectedCategory('All');
                setSelectedAudience('All');
                setSearchQuery('');
              }}
            >
              Clear All Filters
            </Button>
          </Box>
        )}
      </Container>

      {/* Professional Contact Section */}
      {tabValue === 1 && (
        <Box sx={{ backgroundColor: '#f1f5f9', py: 8 }}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: '#1e3a8a' }}>
                  Need Professional Support?
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Our experienced team provides professional services, training, and consultation for title professionals throughout New Jersey.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    component={Link}
                    to="/contact"
                    sx={{
                      background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    Professional Services
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    href="mailto:madtitleagency@gmail.com"
                    sx={{
                      borderColor: '#059669',
                      color: '#059669',
                      px: 4,
                      py: 1.5,
                    }}
                  >
                    Email Us
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Professional Services Include:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    <Typography component="li" sx={{ mb: 1 }}>Custom training and education programs</Typography>
                    <Typography component="li" sx={{ mb: 1 }}>Title examination and abstracting services</Typography>
                    <Typography component="li" sx={{ mb: 1 }}>Compliance consulting and auditing</Typography>
                    <Typography component="li" sx={{ mb: 1 }}>Technology integration support</Typography>
                    <Typography component="li">Professional certification assistance</Typography>
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}

      {/* CTA Section */}
      <Box sx={{ backgroundColor: '#f8fafc', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" component="h2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
              {tabValue === 0 ? 'Need Personalized Help?' : 'Ready to Partner With Us?'}
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary' }}>
              {tabValue === 0 
                ? 'Our experts are here to answer your questions and guide you through the process.'
                : 'Join our network of professional partners and access exclusive resources and training.'
              }
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                {tabValue === 0 ? 'Contact Our Experts' : 'Partner With Us'}
              </Button>
              <Button
                variant="outlined"
                size="large"
                href="tel:+17328745425"
                sx={{
                  borderColor: '#1e3a8a',
                  color: '#1e3a8a',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                }}
              >
                Call Now
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default ResourceCenter; 